import { reactive } from 'vue';
import {
  criteriaDefaults,
  fromQueryParams,
  toQueryParams,
  hasCriteriaParams,
  withTrackingParams
} from './criteriaContract';
import { readSharedLocation, writeSharedLocation } from './sharedLocation';

const STORAGE_KEY = 'whoon.searchCriteria';

// Stored as the canonical query string so reading it goes through the same
// validation as a shared link (K4: query string wins over localStorage).
const fromStorage = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored ? fromQueryParams(new URLSearchParams(stored)) : null;
  } catch {
    return null;
  }
};

const initialCriteria = () => {
  const searchParams = new URLSearchParams(window.location.search);
  // A shared link is complete on its own: an absent location there means
  // "nowhere", not "wherever this browser searched last".
  if (hasCriteriaParams(searchParams)) return fromQueryParams(searchParams);

  const criteria = fromStorage() || criteriaDefaults();
  const shared = readSharedLocation();

  return shared ? Object.assign(criteria, shared) : criteria;
};

const criteria = reactive(initialCriteria());

export const useCriteria = () => ({
  criteria,
  update: (patch) => {
    Object.assign(criteria, patch);
    // Every path that sets a place goes through here (popover, sheet, clear
    // button) and always carries loc; a radius change travels too, but only
    // while a place is set, otherwise there is nothing to share. reset()
    // deliberately does not: a region page resets on load and must not wipe
    // the other page's search area.
    if ('loc' in patch || ('radius' in patch && criteria.loc)) writeSharedLocation(criteria);
  },
  reset: () => Object.assign(criteria, criteriaDefaults()),
  // Tracking params (GA/Ads/UTM) live in the shown URL, not in localStorage:
  // persist() below uses toQueryParams directly, so a stored search never
  // replays a stale tracking param from a previous visit.
  toQueryString: (rangeBounds) =>
    withTrackingParams(
      toQueryParams(criteria, rangeBounds),
      new URLSearchParams(window.location.search)
    ).toString(),
  persist: (rangeBounds) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, toQueryParams(criteria, rangeBounds).toString());
    } catch {
      /* localStorage unavailable: prefill is lost, search keeps working */
    }
  }
});
